import {useDeferredValue, useMemo} from 'react'

import type {ImageReference} from '@github-ui/copilot-chat-types'

import {extractImageUrls} from '../utils/markdown-images'
import {buildImageReference, isGitHubAssetUrl} from '../utils/image-references'
import {MAX_TASK_IMAGES} from './use-markdown-image-upload'

export interface UseComposerImageReferencesOptions {
  /** Current textarea text content. */
  text: string
}

export interface UseComposerImageReferencesReturn {
  /** One ImageReference per GitHub-asset image detected in the text, de-duplicated by URL. */
  imageReferences: ImageReference[]
  /** Whether the number of detected images has reached MAX_TASK_IMAGES. */
  isAtImageLimit: boolean
}

/**
 * Derives image references from prompt text by parsing GitHub-asset image URLs
 * out of the markdown, de-duplicated by URL.
 */
export function useComposerImageReferences({
  text,
}: UseComposerImageReferencesOptions): UseComposerImageReferencesReturn {
  const deferredText = useDeferredValue(text)
  const imageReferences = useMemo(() => {
    const githubImages = extractImageUrls(deferredText).filter(({url}) => isGitHubAssetUrl(url))
    const references: ImageReference[] = []
    const seen = new Set<string>()
    for (const {url, alt} of githubImages) {
      if (seen.has(url)) continue
      seen.add(url)
      references.push(buildImageReference(url, alt))
    }
    return references
  }, [deferredText])
  return {
    imageReferences,
    isAtImageLimit: imageReferences.length >= MAX_TASK_IMAGES,
  }
}
